/**
 * @fileoverview Writes a plant's changed notes or photographs straight into the plants
 * cache, so a card shows the change without the garden being fetched again.
 *
 * Kept out of the components because the journal, the photo dialogs and the
 * card all need the same writes, and each must leave the rest of the plant
 * untouched.
 *
 * Where: the journal (NotesSection), the photo note (usePhotoNote), the
 * check-up's "Save to notes" (PlantDoctor), the card's photos
 * (usePlantPhotos).
 * API: GET /notes/:plantObjId (refreshPlantNotes only; it also marks the
 * season brief stale, seasonWritten, so the note reaches the diary: W24 Job H).
 * Watch out: it writes into every ['plants'…] query at once
 * (setQueriesData), so the active and archived lists both stay in step.
 *
 * @module plantNotesCache
 */

import type { QueryClient } from '@tanstack/react-query';
import { getNotes, type Note } from '@/shared/api/notes';
import type { PlantPhoto } from '@/shared/types/photo';
import { seasonWritten } from '@/shared/lib/briefFreshness';

/**
 * As much of a plant as keeping its notes in step requires.
 *
 * Deliberately minimal: the cache holds the whole plant and spreading it
 * leaves every other field untouched, so naming them here would only invite
 * drift.
 */
interface PlantWithNotes {
  /** The plant's id. */
  plant_obj_id: number;
  /** Its notes, as the plants payload carries them. */
  notes?: Note[];
}

/**
 * Puts fresh notes into the plants cache without fetching the garden.
 *
 * The plants payload carries its own copy of every plant's notes, and the card
 * reads it — for the journal, and for the words shown on a photo. Invalidating
 * that key refetches every plant and re-renders every card, which reads as the
 * page reloading under you, so the rows are written straight in instead.
 *
 * Both places that change a note need this: the journal on the card, and the
 * photo dialog. A photo note edited in the dialog was landing in the database
 * and nowhere else, so the picture kept its old words until something else
 * happened to refetch the garden.
 *
 * @param queryClient - The client holding the plants queries.
 * @param plantObjId - The plant whose notes changed.
 * @param notes - That plant's notes as they now stand.
 */
export function applyNotesToPlantsCache(
  queryClient: QueryClient,
  plantObjId: number,
  notes: Note[]
): void {
  queryClient.setQueriesData<PlantWithNotes[]>(
    { queryKey: ['plants'] },
    (garden) => (Array.isArray(garden)
      ? garden.map((plant) => (
        plant.plant_obj_id === plantObjId ? { ...plant, notes } : plant
      ))
      : garden)
  );
}

/**
 * Reads a plant's notes again after a note was written somewhere other than
 * its journal (the check-up's "Save to notes"), and puts them where the card
 * reads them; the season brief, which the Seasons tab reads, is marked stale.
 *
 * The card's journal keeps its own query (['notes', 'plant', id]) that is
 * fresh for five minutes, so a note written elsewhere stayed off the card
 * until the page was reloaded.
 *
 * @param queryClient - The client holding the notes and plants queries.
 * @param plantObjId - The plant a note was written on.
 * @returns Once both copies hold the fresh notes.
 */
export async function refreshPlantNotes(queryClient: QueryClient, plantObjId: number): Promise<void> {
  const fresh = await getNotes(plantObjId);
  queryClient.setQueryData<Note[]>(['notes', 'plant', plantObjId], fresh);
  applyNotesToPlantsCache(queryClient, plantObjId, fresh);
  seasonWritten(queryClient);
}

/**
 * Puts a plant's photographs into the plants cache.
 *
 * The card keeps its own copy of the list so that an upload or a deletion
 * shows the instant it happens, but the cache is what that copy is re-seeded
 * from whenever anything re-renders it — and until now the two could
 * disagree. Saving a photo with a note wrote the notes into the cache, which
 * changed its identity, which re-seeded the card from a photo list the server
 * had not heard about the new picture in: the photograph that had just been
 * uploaded disappeared off the card a moment after appearing on it.
 *
 * Writing both keeps one story. Same reasoning as the notes above — straight
 * in rather than invalidated, because refetching the garden re-renders every
 * card and reads as the page reloading under you.
 *
 * @param queryClient - The client holding the plants queries.
 * @param plantObjId - The plant whose photographs changed.
 * @param photos - That plant's photographs as they now stand.
 */
export function applyPhotosToPlantsCache(
  queryClient: QueryClient,
  plantObjId: number,
  photos: PlantPhoto[]
): void {
  queryClient.setQueriesData<Array<PlantWithNotes & { photos?: unknown }>>(
    { queryKey: ['plants'] },
    (garden) => (Array.isArray(garden)
      ? garden.map((plant) => (
        plant.plant_obj_id === plantObjId ? { ...plant, photos } : plant
      ))
      : garden)
  );
}
